import React, { useCallback, useMemo, useState } from "react"
import { MenuItem } from "@mui/material"
import {
    CreateRoomResourceForm,
    ReminderType,
    ResourceNotification,
    ResourceNotificationForm,
} from "../../../resourcesTypes"
import { LocalizedEnum, LocalizedStrict } from "../../../../../../../localization/components/AppLocalized"
import { makeStyles } from "tss-react/mui"

import { ApiQueryTable } from "../../../../../../../../core/api/components/ApiQueryTable"
import { useFormikContext } from "formik"
import { useColumnFactory } from "../../../../../../../../core/api/useTableColumns"
import { Box } from "@mui/system"
import { StandardButton } from "../../../../../../../../shared/components/buttons/StandardButton"
import { AddReminderDialog } from "./AddReminderDialog"
import { MRT_ColumnDef, MRT_Row } from "material-react-table"
import { getReminderTimeLabel } from "./utils"
import { useAppLocalization } from "../../../../../../../localization/components/AppLocalizationProvider"

const useStyles = makeStyles()((theme) => ({
    tablePaper: {
        boxShadow: "none",
    },
    tableHeadCell: {
        border: "none",
        color: theme.palette.tailwind.slate[500],
    },
    tableHeadRow: {
        boxShadow: "none",
        background: theme.palette.tailwind.slate[50],
    },
}))

export const NotificationsTable = () => {
    const { classes } = useStyles()
    const [editNotification, setEditNotification] = useState<ResourceNotification | null>(null)
    const [shodAddDialog, setShodAddDialog] = useState(false)
    const { l10n } = useAppLocalization()
    const { setFieldValue, values } = useFormikContext<CreateRoomResourceForm>()

    const filterNotifications = (notificationId: number) => {
        const filtered = values.notifications.filter(({ id }) => id !== notificationId)
        setFieldValue("notifications", [...filtered])
    }

    const columnFactory = useColumnFactory<ResourceNotification>()
    const columns = useMemo<MRT_ColumnDef<ResourceNotification>[]>(
        () => [
            columnFactory.shortNameColumn({
                header: l10n.getString("resources-create-edit-room-general-tab-table-header-type"),
                accessorKey: "type",
                accessorFn: (row) => (
                    <LocalizedEnum base="reminder-type-option" enumValue={row.type}>
                        <span>{row.type}</span>
                    </LocalizedEnum>
                ),
            }),
            columnFactory.shortNameColumn({
                header: l10n.getString("resources-create-edit-room-general-tab-table-header-sent"),
                accessorFn: (row) =>
                    getReminderTimeLabel(row.sendInMinutesBeforeMeeting, row.sendInMinutesAfterMeeting),
            }),
            columnFactory.shortNameColumn({
                header: l10n.getString("resources-create-edit-room-general-tab-table-header-content"),
                accessorKey: "message",
                accessorFn: (row) =>
                    row.type === ReminderType.MEETING_CONFIRMATION ? "Confirmation for booking" : row.message,
            }),
        ],
        [l10n, columnFactory],
    )

    const renderRowActionMenuItems = useCallback(
        ({ closeMenu, row }: { row: MRT_Row<ResourceNotification>; closeMenu: () => void }) => [
            <MenuItem
                key={`edit-${row.original.id}`}
                onClick={() => {
                    setEditNotification(row.original)
                    setShodAddDialog(true)
                    closeMenu()
                }}
            >
                <LocalizedStrict id="tab-availability-table-edit-action">
                    <span>Edit</span>
                </LocalizedStrict>
            </MenuItem>,
            <MenuItem
                key={`delete-${row.original.id}`}
                onClick={() => {
                    filterNotifications(row.original.id)
                    closeMenu()
                }}
            >
                <LocalizedStrict id="tab-availability-table-delete-action">
                    <span>Delete</span>
                </LocalizedStrict>
            </MenuItem>,
        ],
        [setFieldValue, values.notifications],
    )

    const addNewReminder = (newItem: ResourceNotificationForm) => {
        setFieldValue("notifications", [...values.notifications, newItem])
        setShodAddDialog(false)
    }

    return (
        <>
            <ApiQueryTable
                isLoading={false}
                isFetching={false}
                isError={false}
                data={values.notifications}
                columns={columns}
                enableClickToCopy={false}
                enableColumnOrdering
                enableStickyHeader
                enableDensityToggle={false}
                initialState={{ density: "spacious" }}
                enableHiding={false}
                enableRowActions
                enableBottomToolbar={false}
                muiTablePaperProps={{ className: classes.tablePaper }}
                muiTableHeadCellProps={{ className: classes.tableHeadCell }}
                muiTableHeadRowProps={{ className: classes.tableHeadRow }}
                positionActionsColumn="last"
                enableColumnDragging={false}
                renderRowActionMenuItems={renderRowActionMenuItems}
                renderTopToolbar={<></>}
            />
            <Box>
                <LocalizedStrict id="resources-create-room-tab-general-add-reminder-button-label">
                    <StandardButton
                        onClick={() => setShodAddDialog(true)}
                        color="blue"
                        fullWidth={false}
                        variant="contained"
                    >
                        Add reminder
                    </StandardButton>
                </LocalizedStrict>
                {shodAddDialog && (
                    <AddReminderDialog
                        open={shodAddDialog}
                        notification={editNotification}
                        notifications={values.notifications}
                        onClose={() => setShodAddDialog(false)}
                        onAdd={addNewReminder}
                    />
                )}
            </Box>
        </>
    )
}
